import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocStepIndicator,
  DocCodeBlock,
  DocNavFooter,
} from "@/components/docs";
import { docsColors } from "@/lib/docs-theme";

export default function OpsgenieIntegrationPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Notification Methods", href: "/docs/notifications" },
    { label: "Opsgenie" },
  ];

  return (
    <DocLayout
      title="Opsgenie Integration - Uptime Monitor Documentation"
      description="Configure Opsgenie Alert API v2 integration with priority levels, responders, and tags."
      canonical="https://uptime-monitor.io/docs/notifications/opsgenie"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Opsgenie Integration"
        description="Alert API v2 integration with automatic alert lifecycle management, responder routing, and multi-region support."
      />

      {/* Technical Details */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
            Integration Details
          </h2>
          <p className={`${docsColors.text.body} mb-4`}>
            Uptime Monitor uses Opsgenie Alert API v2 with the following behavior:
          </p>
          <ul className={`space-y-2 ${docsColors.text.body}`}>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Alert alias format:
              <code className="px-2 text-green-800 dark:text-green-400">{`{monitor_id}-{incident_id}`}</code>
              for deduplication
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Source field: <code className=" px-1 rounded">uptime-monitor</code>
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              User field: <code className=" px-1 rounded">uptime-monitor-system</code>
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Default tags: <code className=" px-1 rounded">uptime-monitor</code>,{" "}
              <code className=" px-1 rounded">{`{monitor_type}`}</code>
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Actions included: &quot;View Dashboard&quot;, &quot;Check Logs&quot;
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Entity field: monitor name
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* API Configuration */}
      <DocSection title="API Configuration">
        <div className="mb-8">
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
              Required API Permissions
            </h3>
            <p className={`${docsColors.text.body} mb-2`}>
              Your API key (GenieKey) must have these permissions:
            </p>
            <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-4`}>
              <li>• Create alerts</li>
              <li>• Close alerts</li>
              <li>• Update alerts</li>
            </ul>
          </DocCard>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>US Instance</h3>
            <p className={`${docsColors.text.body} text-sm`}>
              Base URL:{" "}
              <code className="dark:bg-gray-700 bg-gray-200 px-1 rounded">
                https://api.opsgenie.com
              </code>
            </p>
          </DocCard>

          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>EU Instance</h3>
            <p className={`${docsColors.text.body} text-sm`}>
              Base URL:{" "}
              <code className="dark:bg-gray-700 bg-gray-200 px-1 rounded">
                https://api.eu.opsgenie.com
              </code>
            </p>
          </DocCard>
        </div>
      </DocSection>

      {/* Configuration Fields */}
      <DocSection title="Configuration Fields">
        <DocCard>
          <div className="space-y-4">
            <div>
              <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>API Key (Required)</h4>
              <p className={`${docsColors.text.body} text-sm`}>
                Your Opsgenie GenieKey for authentication.
              </p>
            </div>

            <div>
              <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>Priority (Optional)</h4>
              <p className={`${docsColors.text.body} text-sm mb-2`}>
                Default priority for all alerts from this contact:
              </p>
              <ul className="space-y-1 text-sm dark:text-gray-400 text-gray-800 ml-4 list-disc list-inside">
                <li>P1 - Critical</li>
                <li>P2 - High</li>
                <li>P3 - Moderate</li>
                <li>P4 - Low</li>
                <li>P5 - Informational</li>
              </ul>
              <p className={`${docsColors.text.body} text-sm mt-2`}>
                Certificate expiration alerts default to P2 if not specified.
              </p>
            </div>

            <div>
              <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>
                Responders (Optional)
              </h4>
              <p className={`${docsColors.text.body} text-sm mb-2`}>
                Currently supports configuration via API. Frontend support coming soon. Responder
                types:
              </p>
              <ul className="space-y-1 text-sm dark:text-gray-400 text-gray-800 ml-4 list-disc list-inside">
                <li>team</li>
                <li>user</li>
                <li>escalation</li>
                <li>schedule</li>
              </ul>
            </div>

            <div>
              <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>Tags (Optional)</h4>
              <p className={`${docsColors.text.body} text-sm`}>
                Comma-separated custom tags. These are added in addition to the default tags.
              </p>
            </div>

            <div>
              <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>
                Auto-Close Alerts (Default: Enabled)
              </h4>
              <p className={`${docsColors.text.body} text-sm`}>
                When enabled, alerts are automatically closed via{" "}
                <code className="dark:bg-gray-700 bg-gray-200 px-1 rounded">
                  /v2/alerts/{`{alias}`}/close
                </code>{" "}
                when monitors recover. Close note includes recovery duration.
              </p>
            </div>

            <div>
              <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>
                EU Instance (Default: Disabled)
              </h4>
              <p className={`${docsColors.text.body} text-sm`}>
                Routes requests to{" "}
                <code className="dark:bg-gray-700 bg-gray-200 px-1 rounded">
                  api.eu.opsgenie.com
                </code>{" "}
                instead of{" "}
                <code className="dark:bg-gray-700 bg-gray-200 px-1 rounded">api.opsgenie.com</code>.
              </p>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Alert Payload Structure */}
      <DocSection title="Alert Payload Structure">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>Monitor down alerts include:</p>

          <DocCodeBlock className="overflow-x-auto">
            <pre className={`text-sm ${docsColors.text.body}`}>
              <code>{`{
  "message": "{monitor.name} is DOWN",
  "alias": "{monitor.id}-{incident.id}",
  "description": "Monitor is not responding. Reason: {incident.reason}",
  "priority": "P2",  // If configured
  "source": "uptime-monitor",
  "entity": "{monitor.name}",
  "user": "uptime-monitor-system",
  "note": "Alert created by uptime-monitor.io",
  "tags": ["uptime-monitor", "{monitor_type}", ...custom_tags],
  "actions": ["View Dashboard", "Check Logs"],
  "details": {
    "monitor_id": "{monitor.id}",
    "monitor_name": "{monitor.name}",
    "monitor_url": "{monitor.settings}",  // Full URL/endpoint
    "monitor_type": "HTTPS|TCP|Ping",
    "monitor_dashboard": "https://{env.website_url}/dashboard/monitors/{monitor.id}",
    "incident_id": "{incident.id}",
    "region": "{first_failed_region}",
    "error_message": "{incident.reason}",
    "check_interval": {monitor.check_interval},
    "timeout": {monitor.timeout}
  },
  "responders": [...]  // If configured
}`}</code>
            </pre>
          </DocCodeBlock>

          <DocAlert variant="info" className="mt-4 p-4">
            <p className={`text-sm ${docsColors.text.body}`}>
              The <code className="dark:bg-gray-700 bg-gray-200 px-1 rounded">details</code> field
              contains custom properties accessible in Opsgenie for filtering, reporting, and
              automation.
            </p>
          </DocAlert>
        </DocCard>
      </DocSection>

      {/* Certificate Alerts */}
      <DocSection title="Certificate Expiration Alerts">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            Certificate alerts use a different structure:
          </p>

          <DocCodeBlock className="overflow-x-auto">
            <pre className={`text-sm ${docsColors.text.body}`}>
              <code>{`{
  "message": "{period.message(host)}",  // e.g., "Certificate for example.com expires in 7 days"
  "alias": "cert-{monitor.id}-{days_until_expiry}",
  "description": "SSL certificate for {host} will expire in {days} days. Please renew it to avoid service disruption.",
  "priority": "P2",  // Default if not configured
  "tags": ["uptime-monitor", "cert-expiry", ...custom_tags],
  "entity": "{host}",
  "actions": ["Renew Certificate"],
  "details": {
    "monitor_id": "{monitor.id}",
    "host": "{host}",
    "port": {port},
    "days_until_expiry": {days},
    "cert_expires_at": "{timestamp}",
    "certificate_type": "ssl"
  }
}`}</code>
            </pre>
          </DocCodeBlock>
        </DocCard>
      </DocSection>

      {/* Alert Close Behavior */}
      <DocSection title="Alert Closure">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            When auto-close is enabled and a monitor recovers:
          </p>

          <DocCodeBlock className="overflow-x-auto">
            <pre className={`text-sm ${docsColors.text.body}`}>
              <code>{`POST /v2/alerts/{alias}/close?identifierType=alias

{
  "user": "uptime-monitor-system",
  "source": "uptime-monitor",
  "note": "Monitor {monitor.name} recovered after {duration} downtime"
}`}</code>
            </pre>
          </DocCodeBlock>

          <p className={`${docsColors.text.body} text-sm mt-4`}>
            Duration is formatted as human-readable text (e.g., &quot;2 hours 15 minutes&quot;).
          </p>
        </DocCard>
      </DocSection>

      {/* Known Limitations */}
      <DocSection title="Known Limitations">
        <DocAlert variant="warning" className="p-6">
          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
            Current Limitations
          </h3>
          <ul className="space-y-2 text-sm dark:text-gray-400 text-gray-800">
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Responder configuration is not yet available in the UI (API only)
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              visibleTo field is not implemented
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Starter plan (10 monitors) limited to 1 Opsgenie contact
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              No support for custom alert actions beyond defaults
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* Troubleshooting */}
      <DocSection title="API Error Codes">
        <DocCard>
          <div className="space-y-4">
            <div>
              <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>401 Unauthorized</h4>
              <p className={`${docsColors.text.body} text-sm`}>
                Invalid API key or insufficient permissions. Verify GenieKey has alert
                create/close/update permissions.
              </p>
            </div>

            <div>
              <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                422 Unprocessable Entity
              </h4>
              <p className={`${docsColors.text.body} text-sm`}>
                Usually indicates invalid responder configuration. Check responder names/IDs exist
                in your Opsgenie account.
              </p>
            </div>

            <div>
              <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                404 Not Found (on close)
              </h4>
              <p className={`${docsColors.text.body} text-sm`}>
                Alert with given alias doesn&apos;t exist or was already closed. This is logged but
                not treated as an error.
              </p>
            </div>

            <div>
              <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                429 Too Many Requests
              </h4>
              <p className={`${docsColors.text.body} text-sm`}>
                Opsgenie rate limit exceeded. Alerts will be retried automatically.
              </p>
            </div>
          </div>
        </DocCard>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/notifications/incident-io", text: "incident.io" }}
        next={{ href: "/docs/notifications/zendesk", text: "Zendesk" }}
      />
    </DocLayout>
  );
}
